/*
 * ----------------------------------------------------------------------------------------------------
 * DELIVERY DATE  (<delivery-date>)  --  CRO 0150, issue #388
 * ----------------------------------------------------------------------------------------------------
 * Renders the DHL delivery date line under the ATC button on DE product pages
 * ("Lieferung am Dienstag, 29.07").
 *
 * WHY THIS IS CLIENT-SIDE AND NOT LIQUID -- this is a correctness requirement, not a preference.
 * A date computed in Liquid becomes part of the rendered page, and therefore part of what gets
 * cached: a visitor arriving at 14:00 can be served a page rendered at 12:00, and would then see a
 * delivery date that is one day too early -- exactly on the wrong side of the 13:00 cutoff. The
 * clock that matters is the DHL depot's, not the visitor's, so the timezone is stated explicitly
 * (Europe/Berlin) rather than taken from the browser.
 *
 * THE RULES (from the ticket)
 *   - DHL, ordered before 13:00 Europe/Berlin on a working day  ->  handed over the same day.
 *   - Otherwise                                                 ->  handed over the next working day.
 *   - Delivery is the first working day AFTER the hand-over day.
 *   - Weekends and NATIONAL German holidays are not working days.
 *
 * Both stated rules fall out of that one model, which is why it is written this way instead of as a
 * table of cases: "ordered before 13:00 = next working day" (Monday 12:00 -> Tuesday) and "weekend
 * orders = Tuesday" (Saturday or Sunday -> hand over Monday -> deliver Tuesday) are the same
 * mechanism seen from two starting points.
 *
 * Deliberately conservative: Saturday counts as a non-delivery day even though DHL does deliver on
 * Saturdays. A Friday-morning order therefore promises Monday. That follows the ticket, which says
 * "working day", and it keeps the failure direction right -- a late promise costs nothing, an early
 * one is a broken one.
 *
 * State holidays are NOT excluded: the delivery date depends on the address, and the PDP does not
 * know the address. Only the nine nationwide (bundesweit) holidays are.
 *
 * FAILURE DIRECTION: the element ships hidden and this script is what reveals it. If anything here
 * throws, or the script never loads, the line simply does not appear -- no date is better than a
 * wrong one.
 * ----------------------------------------------------------------------------------------------------
 */

(() => {
  const TIME_ZONE = 'Europe/Berlin';
  const CUTOFF_HOUR = 13;

  /* A calendar day is held as a Date at 12:00 UTC. Noon keeps every arithmetic result inside the
     same calendar day regardless of DST, so adding days can never slip across midnight. */
  const dayOf = (year, month, day) => new Date(Date.UTC(year, month - 1, day, 12));

  const addDays = (day, count) => {
    const next = new Date(day.getTime());
    next.setUTCDate(next.getUTCDate() + count);
    return next;
  };

  const keyOf = (day) => `${day.getUTCFullYear()}-${day.getUTCMonth() + 1}-${day.getUTCDate()}`;

  /* Now, as seen from the DHL depot rather than from the visitor's device. `at` exists so the test
     suite can hand in a fixed instant; nothing in the theme passes it. */
  const berlinNow = (at = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(at);

    const found = {};
    for (const part of parts) found[part.type] = part.value;

    return {
      day: dayOf(Number(found.year), Number(found.month), Number(found.day)),
      /* h23 yields 00-23, but an engine falling back to h24 would yield 24 for midnight. */
      hour: Number(found.hour) % 24,
    };
  };

  /* Easter Sunday, Meeus/Jones/Butcher. Computed rather than tabulated: a table of holidays expires
     silently at the turn of the year, and a silent expiry here means a wrong delivery date. */
  const easterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return dayOf(year, month, day);
  };

  const holidayCache = new Map();

  /* The nine nationwide German holidays. Easter Sunday and Whit Sunday are Sundays already and are
     therefore left out -- the weekend rule covers them. */
  const holidaysFor = (year) => {
    if (holidayCache.has(year)) return holidayCache.get(year);

    const easter = easterSunday(year);
    const days = [
      dayOf(year, 1, 1), // Neujahr
      addDays(easter, -2), // Karfreitag
      addDays(easter, 1), // Ostermontag
      dayOf(year, 5, 1), // Tag der Arbeit
      addDays(easter, 39), // Christi Himmelfahrt
      addDays(easter, 50), // Pfingstmontag
      dayOf(year, 10, 3), // Tag der Deutschen Einheit
      dayOf(year, 12, 25), // 1. Weihnachtstag
      dayOf(year, 12, 26), // 2. Weihnachtstag
    ];

    const keys = new Set(days.map(keyOf));
    holidayCache.set(year, keys);
    return keys;
  };

  const isWorkingDay = (day) => {
    const weekday = day.getUTCDay();
    if (weekday === 0 || weekday === 6) return false;
    return !holidaysFor(day.getUTCFullYear()).has(keyOf(day));
  };

  const nextWorkingDay = (day) => {
    let candidate = addDays(day, 1);
    /* A bound rather than a while(true): a bug in the holiday set must not hang the page. Nine
       consecutive non-working days do not occur, so 14 is unreachable in practice. */
    for (let step = 0; step < 14; step += 1) {
      if (isWorkingDay(candidate)) return candidate;
      candidate = addDays(candidate, 1);
    }
    return null;
  };

  const deliveryDay = (at = new Date()) => {
    const now = berlinNow(at);

    let handover = now.day;
    if (!isWorkingDay(handover) || now.hour >= CUTOFF_HOUR) {
      handover = nextWorkingDay(handover);
    }
    if (!handover) return null;

    return nextWorkingDay(handover);
  };

  /* "Dienstag, 29.07" -- weekday from Intl so it follows German naming, the numbers formatted by
     hand because de-DE would append a trailing dot and a year we do not want here. */
  const formatDay = (day) => {
    const weekday = new Intl.DateTimeFormat('de-DE', {
      weekday: 'long',
      timeZone: 'UTC',
    }).format(day);
    const dayNumber = String(day.getUTCDate()).padStart(2, '0');
    const monthNumber = String(day.getUTCMonth() + 1).padStart(2, '0');
    return `${weekday}, ${dayNumber}.${monthNumber}`;
  };

  class DeliveryDate extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
      this._onVisibilityChangedListener = this._render.bind(this);
    }

    connectedCallback() {
      this._availableVariants = new Set(
        (this.getAttribute('data-available-variants') || '')
          .split(',')
          .map((id) => Number(id.trim()))
          .filter((id) => Number.isFinite(id) && id > 0)
      );
      this._variantId = Number(this.getAttribute('data-selected-variant')) || null;
      this._output = this.querySelector('[data-delivery-date-text]');

      document.forms[this.getAttribute('form')]?.addEventListener(
        'variant:change',
        this._onVariantChangedListener
      );
      /* A page left open across 13:00 would otherwise keep showing the pre-cutoff date. */
      document.addEventListener('visibilitychange', this._onVisibilityChangedListener);

      this._render();
    }

    disconnectedCallback() {
      document.forms[this.getAttribute('form')]?.removeEventListener(
        'variant:change',
        this._onVariantChangedListener
      );
      document.removeEventListener('visibilitychange', this._onVisibilityChangedListener);
    }

    _onVariantChanged(event) {
      this._variantId = event.detail.variant ? event.detail.variant.id : null;
      this._render();
    }

    _render() {
      if (document.visibilityState === 'hidden') return;

      /* Only for a variant that is deliverable right now. On a sold-out or backorder variant any
         date we name is a promise logistics has not made. */
      if (!this._variantId || !this._availableVariants.has(this._variantId)) {
        this.hidden = true;
        return;
      }

      const day = deliveryDay();
      if (!day || !this._output) {
        this.hidden = true;
        return;
      }

      const template = this.getAttribute('data-template') || '';
      this._output.textContent = template.replace('@@', formatDay(day));
      this.hidden = false;
    }
  }

  if (!window.customElements.get('delivery-date')) {
    window.customElements.define('delivery-date', DeliveryDate);
  }

  /* The rules above are the whole point of this feature and they are not observable from the markup:
     a wrong cutoff or a missed holiday renders a line that looks perfectly fine and states the wrong
     day. So the pure functions are exposed for scripts/tests/delivery-date.tests.ps1 to assert with a
     fixed clock. Nothing in the theme reads this, and removing it breaks that suite by design. */
  window.deliveryDateInternals = { berlinNow, deliveryDay, formatDay, isWorkingDay, easterSunday };
})();

